import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { cx } from "../../ui";
import { IconButton } from "../../ui/icons";
import { channelLabel, sortChannels, type Channel, type Member } from "./model";
import { NewChannelForm, StartDmForm } from "./NewConversation";

const itemClass =
  "flex min-h-9 items-center gap-2 rounded-md px-3 text-sm text-text hover:bg-surface-2 aria-[current=page]:bg-surface-2";

/**
 * Channels and my DMs in one list (channels first), unread ones bold with a dot; two small icon
 * buttons start a channel or a direct message.
 */
export function ChannelList({
  workspaceId,
  myUid,
  channels,
  unread,
  members,
  memberList,
}: {
  workspaceId: string;
  myUid: string;
  channels: Channel[];
  unread: ReadonlySet<string>;
  members: ReadonlyMap<string, Member>;
  memberList: readonly Member[];
}) {
  const [adding, setAdding] = useState<"channel" | "dm" | null>(null);
  const sorted = sortChannels(channels);

  const item = (c: Channel) => {
    const isUnread = unread.has(c.id);
    const label = channelLabel(c, myUid, members);
    return (
      <li key={c.id}>
        <Link
          to="/w/$workspaceId/chat/$channelId"
          params={{ workspaceId, channelId: c.id }}
          className={cx(itemClass, isUnread && "font-semibold")}
        >
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {isUnread ? (
            <span className="size-2 shrink-0 rounded-full bg-accent">
              <span className="sr-only">new messages</span>
            </span>
          ) : null}
        </Link>
      </li>
    );
  };

  return (
    <nav aria-label="Conversations" className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
      <div className="flex items-center justify-end gap-0.5 px-1">
        <IconButton icon="hash" label="New channel" className="size-8" aria-expanded={adding === "channel"} onClick={() => setAdding(adding === "channel" ? null : "channel")} />
        <IconButton icon="newMessage" label="New direct message" className="size-8" aria-expanded={adding === "dm"} onClick={() => setAdding(adding === "dm" ? null : "dm")} />
      </div>
      {adding === "channel" ? <NewChannelForm workspaceId={workspaceId} onDone={() => setAdding(null)} /> : null}
      {adding === "dm" ? <StartDmForm workspaceId={workspaceId} myUid={myUid} members={memberList} onDone={() => setAdding(null)} /> : null}
      <ul className="flex flex-col gap-0.5">
        {sorted.channels.map(item)}
        {sorted.dms.map(item)}
      </ul>
    </nav>
  );
}
